import React, { useEffect } from 'react';
import './Pagination.css';

const Pagination = ({
  currentPage,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onFirstPage,
  loading = false,
  error = null
}) => {
  const handlePreviousClick = () => {
    if (!loading && hasPreviousPage && onPreviousPage) {
      onPreviousPage();
    }
  };

  const handleNextClick = () => {
    if (!loading && hasNextPage && onNextPage) {
      onNextPage();
    }
  };

  const handleFirstPageClick = () => {
    if (!loading && hasPreviousPage && onFirstPage) {
      onFirstPage();
    }
  };

  // Keyboard navigation support
  const handleKeyDown = (event) => {
    // Only handle keyboard events when pagination is focused or active
    if (event.target.closest('.pagination')) {
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          handlePreviousClick();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          handleNextClick();
          break;
        case 'Home':
          event.preventDefault();
          handleFirstPageClick();
          break;
        case 'End':
          event.preventDefault();
          // Could implement "go to last page" functionality if needed
          break;
        default:
          break;
      }
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);



  return (
    <nav 
      className="pagination" 
      role="navigation" 
      aria-label="Photo gallery pagination"
      tabIndex="0"
    >
      <button
        className={`pagination-button pagination-first ${
          !hasPreviousPage || loading ? 'disabled' : ''
        }`}
        onClick={handleFirstPageClick}
        disabled={!hasPreviousPage || loading}
        aria-label="Go to first page"
        aria-describedby="pagination-help"
        type="button"
      >
        {loading ? (
          <span className="pagination-loading">
            <span 
              className="pagination-spinner"
              aria-hidden="true"
            ></span>
            <span className="sr-only">Loading first page</span>
            Loading...
          </span>
        ) : (
          <>
            <span aria-hidden="true">⏮</span>
            <span>First</span>
          </>
        )}
      </button>

      <button
        className={`pagination-button pagination-previous ${
          !hasPreviousPage || loading ? 'disabled' : ''
        }`}
        onClick={handlePreviousClick}
        disabled={!hasPreviousPage || loading}
        aria-label="Go to previous page"
        aria-describedby="pagination-help"
        type="button"
      >
        {loading ? (
          <span className="pagination-loading">
            <span 
              className="pagination-spinner"
              aria-hidden="true"
            ></span>
            <span className="sr-only">Loading previous page</span>
            Loading...
          </span>
        ) : (
          <>
            <span aria-hidden="true">←</span>
            <span>Previous</span>
          </>
        )}
      </button>
      
      <button
        className={`pagination-button pagination-next ${
          !hasNextPage || loading ? 'disabled' : ''
        }`}
        onClick={handleNextClick}
        disabled={!hasNextPage || loading}
        aria-label="Go to next page"
        aria-describedby="pagination-help"
        type="button"
      >
        {loading ? (
          <span className="pagination-loading">
            <span 
              className="pagination-spinner"
              aria-hidden="true"
            ></span>
            <span className="sr-only">Loading next page</span>
            Loading...
          </span>
        ) : (
          <>
            <span>Next</span>
            <span aria-hidden="true">→</span>
          </>
        )}
      </button>
      
      {/* Hidden help text for screen readers */}
      <div id="pagination-help" className="sr-only">
        Use arrow keys to navigate between pages. Left arrow or up arrow for previous page, right arrow or down arrow for next page. Home key for first page.
      </div>
    </nav>
  );
};

export default Pagination;